// HarvestBillingWithStripe.jsx
import React, {useEffect, useState} from "react";
import {
    Card,
    Checkbox,
    Radio,
    Alert,
    Button,
    Form,
    Typography,
    Divider,
    Result,
    Input,
    message
} from "antd";
import {
    CreditCardOutlined,
    DollarOutlined,
    BankOutlined,
    CalendarOutlined,
    FileDoneOutlined,
    CreditCardFilled,
    MobileOutlined
} from "@ant-design/icons";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const harvestOptions = ["January", "April", "July", "October"];

export default function HeroBillingWithStripe() {
    const [harvestMonths, setHarvestMonths] = useState([]);
    const [billingType, setBillingType] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [form] = Form.useForm();
    const [showSummary, setShowSummary] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();

    const nextHarvest = harvestMonths.length > 0 ? harvestMonths[0] : "your next season";
    const billingLabel = {
        monthly: "Monthly billing – pay every month",
        perSeason: "Per Season billing – pay once each growing season",
        postHarvest: `Post-Harvest billing – pay after ${nextHarvest}`
    };

    const handleStripeSubmit = async () => {
        if (!stripe || !elements) return;

        setLoading(true);

        const card = elements.getElement(CardElement);
        const { paymentMethod: stripePaymentMethod, error } = await stripe.createPaymentMethod({
            type: "card",
            card: card,
            billing_details: {
                name: form.getFieldValue("cardHolder") || "Anonymous",
                email,
            }
        });

        if (error) {
            message.error(error.message);
            setLoading(false);
            return;
        }

        // 🔍 Étape de vérification par SetupIntent (0€)
        try {
            //const response = await fetch("https://gateway.assiganto.net/pre-registration/api/setup-intent", {
                const response = await fetch("https://localhost:3001/pre-registration/api/setup-intent", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const { clientSecret } = await response.json();

            const confirmResult = await stripe.confirmCardSetup(clientSecret, {
                payment_method: stripePaymentMethod.id,
            });

            if (confirmResult.error) {
                throw new Error(confirmResult.error.message);
            }

            message.success("Payment method verified successfully ✅");
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            console.error("❌ SetupIntent Error:", err);
            message.error("Payment method verification failed.");
        } finally {
            setLoading(false);
        }
    };


    const handleRealStripePayment = async () => {
        if (!stripe || !elements) return;

        setLoading(true); // 🚫 désactive le bouton pendant le traitement

        const card = elements.getElement(CardElement);
        const { paymentMethod: stripePaymentMethod, error } = await stripe.createPaymentMethod({
            type: "card",
            card: card,
            billing_details: {
                name: form.getFieldValue("cardHolder") || "Anonymous"
            }
        });

        if (error) {
            console.error("Stripe error:", error);
            message.error(error.message);
            setLoading(false); // ✅ réactive le bouton en cas d’erreur
            return;
        }

        try {
            const response = await fetch("https://gateway.assiganto.net/pre-registration/api/subscribe", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    paymentMethodId: stripePaymentMethod.id,
                    plan: "AGRI_HERO",
                    billingCycle: billingType,
                    email: email
                })
            });
            const data = await response.json();
            if (response.ok) {
                setSubmitted(true);
                message.success("Your subscription is confirmed!");
                window.scrollTo({ top: 0, behavior: "smooth" });
                console.log("✅ Subscription created:", data);
            }else{
                throw new Error("Subscription failed");
            }
        } catch (err) {
            console.error("Backend error:", err);
            message.error("Subscription could not be created.");
        } finally {
            setLoading(false); // ✅ libère le bouton à la fin du processus
        }
    };



    const handleCancel = () => {
        navigate("/");
    };

    useEffect(() => {
        if (submitted) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            setTimeout(() => navigate("/"), 5000); // Redirige vers accueil après 5 sec
        }
    }, [submitted]);

    return (
        <div style={{ maxWidth: 600, margin: "0 auto", padding: "24px" }}>
            {submitted ? (
                <Result
                    status="success"
                    title="Subscription Successful!"
                    subTitle="Your AGRI HERO plan is now active."
                    extra={<Button className="application-button" onClick={handleCancel}>Return Home</Button>}
                />
            ) : (
                <div></div>
            )}
            <div style={{ textAlign: "center", marginBottom: 32 }}>
                <Title level={2}>Payment Method</Title>
                <Text type="secondary">
                    Intelligent farming tools tailored to your seasonal cycle.
                </Text>
                <Divider />
            </div>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
                <Alert
                    type="info"
                    message="We’ll verify your payment method with a 0€ authorization, no charges will be made."
                    showIcon
                    style={{ marginBottom: 20 }}
                />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                <Card title={<><CalendarOutlined /> 1. Select Your Harvest Period</>} style={{ borderLeft: "4px solid #52c41a" }}>
                    <Checkbox.Group
                        options={harvestOptions}
                        value={harvestMonths}
                        onChange={setHarvestMonths}
                    />
                </Card>

                <Card title={<><FileDoneOutlined /> 2. Choose a Billing Method</>} style={{ borderLeft: "4px solid #1890ff" }}>
                    <Radio.Group
                        onChange={(e) => setBillingType(e.target.value)}
                        value={billingType}
                    >
                        <Radio value="monthly">Monthly</Radio>
                        <Radio value="perSeason">Per Season</Radio>
                        <Radio value="postHarvest">Post-Harvest</Radio>
                    </Radio.Group>
                </Card>

                {billingType && (
                    <Card title={<><FileDoneOutlined /> 3. Payment Plan Summary</>} style={{ borderLeft: "4px solid #faad14" }}>
                        <Alert
                            type="info"
                            message={billingLabel[billingType]}
                            showIcon
                        />
                    </Card>
                )}

                <Card title={<><CreditCardFilled /> 4. Payment Method</>} style={{ borderLeft: "4px solid #eb2f96" }}>
                    <Radio.Group
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        value={paymentMethod}
                        style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                    >
                        <Radio.Button value="Credit Card">
                            <CreditCardOutlined /> Credit Card
                        </Radio.Button>
                        <Radio.Button value="PayPal">
                            <DollarOutlined /> PayPal
                        </Radio.Button>
                        <Radio.Button value="Bank Transfer">
                            <BankOutlined /> Bank Transfer
                        </Radio.Button>
                        <Radio.Button value="Mobile Payment" style={{ minWidth: "120px", textAlign: "center" }}>
                            <MobileOutlined /> Mobile
                        </Radio.Button>
                    </Radio.Group>

                    {paymentMethod === "Credit Card" && (
                        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
                            <Form.Item label="Card Holder Name" name="cardHolder">
                                <Input placeholder="John Doe" />
                            </Form.Item>
                            <Form.Item label="Card Details">
                                <CardElement options={{ hidePostalCode: true }} />
                            </Form.Item>
                        </Form>
                    )}

                    {paymentMethod === "PayPal" && (
                        <Form layout="vertical" style={{ marginTop: 20 }}>
                            <Form.Item label="PayPal Email">
                                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="example@email.com" />
                            </Form.Item>
                        </Form>
                    )}

                    {paymentMethod === "Bank Transfer" && (
                        <Form layout="vertical" style={{ marginTop: 20 }}>
                            <Form.Item label="Account Holder Name">
                                <Input placeholder="e.g. John Doe" />
                            </Form.Item>
                            <Form.Item label="IBAN / RIB Number">
                                <Input placeholder="FR76 3000 6000 0112 3456 7890 189" />
                            </Form.Item>
                            <Form.Item label="Bank Name">
                                <Input placeholder="Your Bank Name" />
                            </Form.Item>
                        </Form>
                    )}

                    {paymentMethod === "Mobile Payment" && (
                        <Form layout="vertical" style={{ marginTop: 20 }}>
                            <Form.Item label="Mobile Money Number">
                                <Input placeholder="e.g. +228 90 00 00 00" />
                            </Form.Item>
                            <Form.Item label="Mobile Provider">
                                <Input placeholder="e.g. Flooz, T-Money, Orange Money..." />
                            </Form.Item>
                        </Form>
                    )}
                </Card>

                {billingType && harvestMonths.length > 0 && paymentMethod && !showSummary && (
                    <div style={{ textAlign: 'right' }}>
                        <Button className="application-button" onClick={() => setShowSummary(true)}>
                            Continue to Summary
                        </Button>
                    </div>
                )}

                {showSummary && (
                    <Card title="5. Confirm Your Subscription" style={{ borderLeft: "4px solid #722ed1" }}>
                        <p><strong>Harvest Period:</strong> {harvestMonths.join(", ")}</p>
                        <p><strong>Billing Plan:</strong> {billingLabel[billingType]}</p>
                        <p><strong>Payment Method:</strong> {paymentMethod}</p>
                        {/*<div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>*/}
                        {/*    <Button onClick={handleCancel} danger>Cancel and Return Home</Button>*/}
                        {/*    <Button type="primary" onClick={handleStripeSubmit} loading={loading} disabled={loading}>Confirm & Pay</Button>*/}
                        {/*</div>*/}
                    </Card>
                )}
            </div>
        </div>
    );
}
